import React from "react";
import EntitlementWithQuotaRow from "./EntitlementWithQuotaRow";

// Risk Score entitlement section for Manage Sponsor Org, Settings tab. A vendor
// org never gets it: published risk scoring is a sponsor-and-admin surface. The
// parent "Risk Score" toggle is derived from the two children (has_auto_score,
// has_sme_score): toggling it off clears both, toggling it on defaults Auto-Score on.
const PARENT_HELPER =
  "Diligent's risk analysis pipeline. Pick one or both scoring modes below.";

const AUTO_DESC =
  "On VQQ completion, the Diligent Risk Model auto-scores each response. Diligent admin reviews the auto-scored findings and publishes — no gap comments required.";

const SME_DESC =
  "Includes the Diligent Risk Model auto-score, plus a Diligent SME (subject-matter expert) reviews every non-Compliant finding, accepts or edits the severity, and adds a required gap-analysis comment before publishing.";

const OrgRiskEntitlementsSection = ({
  orgData,
  setOrgData,
  isSettingsEditMode,
  isSettingsSaving,
  isSponsorView = false,
}) => {
  if (!isSponsorView) return null;

  const interactive = isSettingsEditMode && !isSettingsSaving;
  const parentChecked = !!orgData.has_auto_score || !!orgData.has_sme_score;

  const handleParentToggle = () => {
    if (!interactive) return;
    if (parentChecked) {
      setOrgData((prev) => ({
        ...prev,
        has_auto_score: false,
        has_sme_score: false,
      }));
    } else {
      setOrgData((prev) => ({ ...prev, has_auto_score: true }));
    }
  };

  const set = (key, value) =>
    setOrgData((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="sm:col-span-2 space-y-3">
      <label
        className={`flex gap-3 ${interactive ? "cursor-pointer" : "cursor-default pointer-events-none"}`}
      >
        <div className="flex h-6 shrink-0 items-center">
          <div className="group grid size-4 grid-cols-1">
            <input
              id="org-setting-risk-score"
              type="checkbox"
              checked={parentChecked}
              readOnly={!interactive}
              onChange={handleParentToggle}
              className="col-start-1 row-start-1 size-4 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            />
            <svg
              viewBox="0 0 16 16"
              fill="white"
              className="pointer-events-none col-start-1 row-start-1 size-4 opacity-0 group-has-[input:checked]:opacity-100"
              aria-hidden
            >
              <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
            </svg>
          </div>
        </div>
        <div className="flex-1">
          <span className="text-sm/6 font-medium text-gray-900">Risk Score</span>
          <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">{PARENT_HELPER}</p>
        </div>
      </label>

      {parentChecked ? (
        <div className="ml-7 space-y-4">
          <EntitlementWithQuotaRow
            id="org-setting-auto-score"
            label="Auto-Score"
            description={AUTO_DESC}
            enabled={!!orgData.has_auto_score}
            quota={orgData.auto_score_quota}
            unlimited={!!orgData.auto_score_unlimited}
            used={orgData.auto_score_used}
            quotaLabel="Auto-scores in contract"
            unlimitedLabel="Unlimited auto-scores"
            isEditMode={isSettingsEditMode}
            isSaving={isSettingsSaving}
            onEnabledChange={(v) => set("has_auto_score", v)}
            onQuotaChange={(v) => set("auto_score_quota", v)}
            onUnlimitedChange={(v) => set("auto_score_unlimited", v)}
          />
          <EntitlementWithQuotaRow
            id="org-setting-sme-score"
            label="SME-Score and Gap Analysis"
            description={SME_DESC}
            enabled={!!orgData.has_sme_score}
            quota={orgData.sme_score_quota}
            unlimited={!!orgData.sme_score_unlimited}
            used={orgData.sme_score_used}
            quotaLabel="SME-scores in contract"
            unlimitedLabel="Unlimited SME-scores"
            isEditMode={isSettingsEditMode}
            isSaving={isSettingsSaving}
            onEnabledChange={(v) => set("has_sme_score", v)}
            onQuotaChange={(v) => set("sme_score_quota", v)}
            onUnlimitedChange={(v) => set("sme_score_unlimited", v)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default OrgRiskEntitlementsSection;
